import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import EventsList from './views/EventsList.jsx';
import EventsCalendar from './views/EventsCalendar.jsx';
import EventsKanban from './views/EventsKanban.jsx';
import EventDetail from './views/EventDetail.jsx';
import UniverseOverrides from './views/UniverseOverrides.jsx';
import UrVenueOverrides from './views/UrVenueOverrides.jsx';
import RefCodes from './views/RefCodes.jsx';
import MetaAudiences from './views/MetaAudiences.jsx';
import FacebookPages from './views/FacebookPages.jsx';
import MetaPerformance from './views/MetaPerformance.jsx';
import UniverseWidget from './views/UniverseWidget.jsx';
import EventDashboard from './views/EventDashboard.jsx';
import EventDashboardConfig from './views/EventDashboardConfig.jsx';
import TenantSettings from './views/TenantSettings.jsx';
import Templates from './views/Templates.jsx';
import RecentlyDeleted from './views/RecentlyDeleted.jsx';
import Activity from './views/Activity.jsx';
import UrlGenerator from './views/UrlGenerator.jsx';
import UniverseListings from './views/UniverseListings.jsx';
import UrVenueListings from './views/UrVenueListings.jsx';
import BandsintownListings from './views/BandsintownListings.jsx';
import TicketWebListings from './views/TicketWebListings.jsx';
import ResidentAdvisorListings from './views/ResidentAdvisorListings.jsx';
import UserAccess from './views/UserAccess.jsx';
import Login from './pages/Login.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import { useAuth } from './contexts/AuthContext.jsx';
import { apiFetch } from './lib/api.js';
import { getStoredTheme, setTheme } from './lib/theme.js';
import { LoadingState } from './lib/Spinner.jsx';
import {
  CalendarDays, Columns3, List as ListIcon, Users, BarChart3, Tag, Link2,
  LayoutDashboard, LayoutTemplate, Activity as ActivityIcon, Settings as SettingsIcon,
  ChevronsLeft, ChevronsRight, Gauge, ExternalLink, SlidersHorizontal, Radio,
  Menu, X, UserCog, Facebook, Ticket, Disc3, Globe, Building2,
} from 'lucide-react';

// Sidebar nav, grouped by purpose. `requires` gates an item on a tenant flag.
const NAV_GROUPS = [
  {
    label: 'Events',
    items: [
      { path: 'list',     label: 'List',     Icon: ListIcon },
      { path: 'calendar', label: 'Calendar', Icon: CalendarDays },
      { path: 'kanban',   label: 'Kanban',   Icon: Columns3 },
      { path: 'event-dashboard',  label: 'Box Office Live',  Icon: Gauge, requires: 'uses_event_dashboard', launch: true },
      { path: 'ticketing-widget', label: 'Ticketing Widget', Icon: LayoutDashboard, requires: 'uses_universe_widget' },
    ],
  },
  {
    label: 'Promote',
    items: [
      { path: 'meta-audiences',   label: 'Meta audiences',   Icon: Users },
      { path: 'facebook-pages', label: 'Pages', Icon: Facebook },
      { path: 'meta-performance', label: 'Meta performance', Icon: BarChart3 },
      { path: 'ref-codes',        label: 'Ref codes',        Icon: Tag },
      { path: 'url-generator',    label: 'URL generator',    Icon: Link2 },
    ],
  },
  {
    label: 'Platforms',
    items: [
      { path: 'universe',         label: 'Universe',         Icon: Globe },
      { path: 'urvenue',          label: 'UrVenue',          Icon: Building2 },
      { path: 'bandsintown',      label: 'Bandsintown',      Icon: Radio },
      { path: 'resident-advisor', label: 'Resident Advisor', Icon: Disc3 },
      { path: 'ticketweb',        label: 'TicketWeb',        Icon: Ticket },
    ],
  },
  {
    label: 'Configure',
    items: [
      { path: 'templates', label: 'Templates', Icon: LayoutTemplate },
      { path: 'event-dashboard-config', label: 'Box Office Live config', Icon: SlidersHorizontal },
      { path: 'activity',  label: 'Activity',  Icon: ActivityIcon },
      { path: 'user-access', label: 'User Access', Icon: UserCog, adminOnly: true },
      { path: 'settings',  label: 'Settings',  Icon: SettingsIcon },
    ],
  },
];

const SIDEBAR_KEY = 'te:sidebar';
function getStoredCollapsed() {
  try { return localStorage.getItem(SIDEBAR_KEY) === 'collapsed'; } catch { return false; }
}
function setStoredCollapsed(v) {
  try { localStorage.setItem(SIDEBAR_KEY, v ? 'collapsed' : 'expanded'); } catch (_) {}
}

// Phase 0 scaffold: bare layout + path-based tenant routing.
// /<tenantSlug>/calendar etc.  -- single SPA, tenant slug resolved from URL.
// Real auth + tab content come in later phases.

// Swap the tab + home-screen icons to the current tenant's favicon_url
// (Settings → Brand). index.html carries the defaults; this mutates the
// id'd <link> tags in place and restores the defaults when no tenant icon
// is set (login pages, tenants without branding).
function useTenantFavicon() {
  const { tenantSlug } = useParams();
  useEffect(() => {
    let on = true;
    const set = (id, href, fallback) => {
      const el = document.getElementById(id);
      if (el) el.href = href || fallback;
    };
    const apply = (url) => {
      set('app-favicon', url, '/LIV_favicon_32x32.png');
      set('app-touch-icon', url, '/pwa-192.png');
    };
    if (!tenantSlug) { apply(null); return; }
    apiFetch('/api/me/tenants')
      .then((r) => r.json())
      .then((d) => {
        if (!on) return;
        const t = (d.tenants || []).find((x) => x.slug === tenantSlug);
        apply(t?.favicon_url || null);
      })
      .catch(() => {});
    return () => { on = false; };
  }, [tenantSlug]);
}

function Layout({ children }) {
  useTenantFavicon();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(getStoredCollapsed);
  const toggleCollapsed = () => setCollapsed((c) => { const n = !c; setStoredCollapsed(n); return n; });
  // Mobile nav drawer (replaces the cramped horizontal tab strip on phones).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]); // close on navigate
  // Unauthenticated → client-side SSO login (chat-portal model).
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  const signOut = logout;
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border px-4 py-2 flex items-center gap-3 md:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="font-semibold whitespace-nowrap">Teliance Events</div>
          <TenantSwitcher />
        </div>
        {/* Desktop-only: search + theme + sign out. On mobile these live in the drawer. */}
        <div className="hidden md:flex flex-1 min-w-0"><Omnibox /></div>
        <div className="hidden md:flex text-sm flex-shrink-0 items-center gap-3">
          <ThemeToggle />
          <span className="text-muted-foreground hidden lg:inline">{user?.email || user?.sub}</span>
          <button onClick={signOut} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Sign out</button>
        </div>
        {/* Mobile-only: hamburger opens the nav drawer. */}
        <button
          className="md:hidden ml-auto p-2 -mr-1 rounded-md hover:bg-accent"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open menu"
        ><Menu size={22} /></button>
      </header>

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div className="absolute top-0 left-0 bottom-0 w-72 max-w-[85%] bg-background border-r border-border shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="font-semibold">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} aria-label="Close menu" className="p-1 rounded-md hover:bg-accent"><X size={20} /></button>
            </div>
            <div className="px-3 py-2 border-b border-border"><Omnibox /></div>
            <div className="flex-1 overflow-y-auto p-3" onClick={() => setMobileNavOpen(false)}>
              <Sidebar />
            </div>
            <div className="border-t border-border p-3 flex items-center justify-between gap-3">
              <ThemeToggle />
              <span className="text-xs text-muted-foreground truncate flex-1 text-right">{user?.email || user?.sub}</span>
              <button onClick={signOut} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent flex-shrink-0">Sign out</button>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 flex">
        <aside className={`hidden md:flex md:flex-col border-r border-border transition-[width] duration-150 ${collapsed ? 'w-16' : 'w-56'}`}>
          <div className="flex-1 overflow-y-auto p-3">
            <Sidebar collapsed={collapsed} />
          </div>
          <button
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="border-t border-border p-2 flex items-center gap-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {collapsed ? <ChevronsRight size={16} className="mx-auto" /> : <><ChevronsLeft size={16} /> <span>Collapse</span></>}
          </button>
        </aside>
        <main className="flex-1 p-4 md:p-6 min-w-0">{children}</main>
      </div>
    </div>
  );
}

function ThemeToggle() {
  const [theme, setT] = useState(getStoredTheme());
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const pick = (v) => { setTheme(v); setT(v); setOpen(false); };
  const label = theme === 'system' ? 'Auto' : theme === 'dark' ? 'Dark' : 'Light';
  const icon = theme === 'system' ? '◐' : theme === 'dark' ? '●' : '○';
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        title={`Theme: ${label}`}
        className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent flex items-center gap-1.5"
      >
        <span>{icon}</span><span>{label}</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 border border-border rounded-md bg-background shadow-lg z-50 min-w-[120px]">
          {[
            { v: 'system', l: 'Auto', i: '◐' },
            { v: 'light', l: 'Light', i: '○' },
            { v: 'dark', l: 'Dark', i: '●' },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => pick(o.v)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent border-b border-border last:border-b-0 flex items-center gap-2 ${o.v === theme ? 'font-medium' : ''}`}
            >
              <span className="w-4">{o.i}</span>
              <span className="flex-1">{o.l}</span>
              {o.v === theme && <span className="text-xs text-muted-foreground">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TenantSwitcher() {
  const { tenantSlug, tab } = useParams();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    apiFetch('/api/me/tenants').then((r) => r.json()).then((d) => setTenants(d.tenants || []));
  }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (tenants.length === 0) return null;
  const current = tenants.find((t) => t.slug === tenantSlug);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent flex items-center gap-1.5">
        {current?.brand_color && <span className="w-2 h-2 rounded-full" style={{ background: current.brand_color }} />}
        {current?.name || tenantSlug || 'Select tenant'}
        <span className="text-muted-foreground">▾</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 border border-border rounded-md bg-background shadow-lg z-50 min-w-[180px]">
          {tenants.map((t) => (
            <button
              key={t.slug}
              onClick={() => {
                navigate(`/${t.slug}/${tab || 'calendar'}`);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent border-b border-border last:border-b-0 flex items-center gap-2 ${t.slug === tenantSlug ? 'font-medium' : ''}`}
            >
              {t.brand_color && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.brand_color }} />}
              <span className="flex-1">{t.name}</span>
              <span className="text-xs text-muted-foreground">{t.role || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Omnibox() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  // Resolve the tenant's display name for the placeholder (falls back to slug).
  const [tenantName, setTenantName] = useState('');
  useEffect(() => {
    if (!tenantSlug) return;
    apiFetch('/api/me/tenants').then((r) => r.json())
      .then((d) => setTenantName((d.tenants || []).find((t) => t.slug === tenantSlug)?.name || ''))
      .catch(() => {});
  }, [tenantSlug]);
  useEffect(() => {
    if (!tenantSlug || q.trim().length < 2) { setResults([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      apiFetch(`/api/tenants/${tenantSlug}/search?q=${encodeURIComponent(q)}`, { signal: ctrl.signal })
        .then((r) => r.json()).then((d) => setResults(d.events || []))
        .catch(() => {});
    }, 200);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [q, tenantSlug]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!tenantSlug) return <div className="hidden md:block flex-1" />;
  return (
    <div ref={ref} className="w-full order-last md:order-none md:w-auto md:flex-1 md:max-w-xl relative">
      <input
        type="text"
        placeholder={`Search events in ${tenantName || tenantSlug}…`}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="w-full px-3 py-1.5 text-sm border border-input rounded-md bg-background"
      />
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 border border-border rounded-md bg-background shadow-lg z-50 max-h-80 overflow-y-auto">
          {results.map((e) => (
            <button
              key={e.id}
              onClick={() => { navigate(`/${tenantSlug}/event/${e.id}`); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent border-b border-border last:border-b-0"
            >
              <div>{e.talent}</div>
              <div className="text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString()} · {e.status}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({ horizontal, collapsed }) {
  const { tenantSlug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [isGlobal, setIsGlobal] = useState(false);
  useEffect(() => {
    if (!tenantSlug) return;
    apiFetch('/api/me/tenants')
      .then((r) => r.json())
      .then((d) => {
        setIsGlobal(d.role === 'global_admin');
        setTenant((d.tenants || []).find((t) => t.slug === tenantSlug) || null);
      })
      .catch(() => {});
  }, [tenantSlug]);
  const { pathname } = useLocation();
  // Match the segment after /:tenantSlug/. EventDetail (/event/:id) is not a
  // sidebar item; highlight its parent tab so context isn't lost on detail
  // and override pages.
  const seg = pathname.match(/^\/[^/]+\/([^/]+)/)?.[1];
  const currentTab = seg === 'event' ? 'calendar' : seg;
  if (!tenantSlug) return null;

  // Hide items gated on a tenant flag when the row says false. Until the
  // tenant loads, show everything (avoids flicker; flag is usually true).
  // adminOnly items are hidden until the caller is confirmed admin/global
  // (default-hide, so they never flash to non-admins; backend enforces too).
  const allow = (item) => {
    if (item.requires && tenant && !tenant[item.requires]) return false;
    if (item.adminOnly && !(isGlobal || tenant?.role === 'admin')) return false;
    // Per-brand section restriction: if this tenant grant lists allowed sections,
    // hide nav items not in it. Globals (no per-brand grant) and unrestricted
    // grants (sections null/empty) see everything.
    if (!isGlobal && Array.isArray(tenant?.sections) && tenant.sections.length
        && !tenant.sections.includes(item.path)) return false;
    return true;
  };

  const NavLink = ({ item }) => {
    const active = currentTab === item.path;
    const { Icon } = item;
    const cls = `flex items-center gap-2.5 rounded-md text-sm transition-colors ${
      horizontal ? 'px-3 py-2 whitespace-nowrap flex-shrink-0'
        : collapsed ? 'px-0 py-2 justify-center' : 'px-3 py-2'
    } ${active ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`;
    // `launch` items open the standalone full-screen app in a new tab (no admin
    // chrome) rather than rendering inside the SPA layout.
    if (item.launch) {
      return (
        <a
          href={`/${tenantSlug}/${item.path}`}
          target="_blank"
          rel="noopener"
          title={collapsed ? item.label : undefined}
          className={cls}
        >
          {Icon && <Icon size={16} className="flex-shrink-0" />}
          {!collapsed && (
            <span className="flex items-center gap-1">
              {item.label}
              <ExternalLink size={12} className="opacity-50 flex-shrink-0" />
            </span>
          )}
        </a>
      );
    }
    return (
      <Link
        to={`/${tenantSlug}/${item.path}`}
        aria-current={active ? 'page' : undefined}
        title={collapsed ? item.label : undefined}
        className={cls}
      >
        {Icon && <Icon size={16} className="flex-shrink-0" />}
        {!collapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  // Mobile: flat horizontal scroller (all items, ignore grouping).
  if (horizontal) {
    return (
      <nav className="flex gap-1">
        {NAV_GROUPS.flatMap((g) => g.items).filter(allow).map((item) => (
          <NavLink key={item.path} item={item} />
        ))}
      </nav>
    );
  }

  // Desktop: grouped sections. Collapsed = icon rail (no headers, thin
  // dividers between groups); expanded = labeled groups.
  return (
    <nav className="space-y-4">
      {NAV_GROUPS.map((group, gi) => {
        const items = group.items.filter(allow);
        if (!items.length) return null;
        return (
          <div key={group.label} className={collapsed && gi > 0 ? 'pt-3 border-t border-border' : ''}>
            {!collapsed && (
              <div className="px-3 mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                {group.label}
              </div>
            )}
            <div className="space-y-0.5">
              {items.map((item) => <NavLink key={item.path} item={item} />)}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

function TenantHome() {
  const { tenantSlug } = useParams();
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">{tenantSlug}</h1>
      <p className="text-muted-foreground">Phase 0 shell, tab content lands in later phases.</p>
    </div>
  );
}

function TabPlaceholder() {
  const { tenantSlug, tab } = useParams();
  return (
    <div>
      <h2 className="text-xl font-semibold mb-2 capitalize">{tab?.replace('-', ' ')}</h2>
      <p className="text-muted-foreground">
        <code>{tenantSlug}</code> · <code>{tab}</code>, not yet built.
      </p>
    </div>
  );
}

function TabRouter() {
  const { tab, tenantSlug } = useParams();
  // Direct-URL guard for section restriction (nav already hides these). Soft:
  // sections are a visibility concern (role still gates data), so we don't block
  // render while checking — just flip to a notice if this tab isn't permitted.
  const [sectionDenied, setSectionDenied] = useState(false);
  useEffect(() => {
    let on = true;
    setSectionDenied(false);
    apiFetch('/api/me/tenants')
      .then((r) => r.json())
      .then((d) => {
        if (!on || d.role === 'global_admin') return;
        const t = (d.tenants || []).find((x) => x.slug === tenantSlug);
        if (Array.isArray(t?.sections) && t.sections.length && !t.sections.includes(tab)) {
          setSectionDenied(true);
        }
      })
      .catch(() => {});
    return () => { on = false; };
  }, [tab, tenantSlug]);
  if (sectionDenied) {
    return <div className="p-4 text-sm text-muted-foreground">You don’t have access to this section.</div>;
  }
  if (tab === 'list') return <EventsList />;
  if (tab === 'calendar') return <EventsCalendar />;
  if (tab === 'kanban') return <EventsKanban />;
  if (tab === 'ref-codes') return <RefCodes />;
  if (tab === 'meta-audiences') return <MetaAudiences />;
  if (tab === 'facebook-pages') return <FacebookPages />;
  if (tab === 'meta-performance') return <MetaPerformance />;
  if (tab === 'ticketing-widget') return <UniverseWidget />;
  if (tab === 'event-dashboard-config') return <EventDashboardConfig />;
  // event-dashboard is a standalone full-page route (see Routes), not a Layout tab.
  if (tab === 'settings') return <TenantSettings />;
  if (tab === 'templates') return <Templates />;
  if (tab === 'url-generator') return <UrlGenerator />;
  if (tab === 'universe') return <UniverseListings />;
  if (tab === 'urvenue') return <UrVenueListings />;
  if (tab === 'bandsintown') return <BandsintownListings />;
  if (tab === 'resident-advisor') return <ResidentAdvisorListings />;
  if (tab === 'ticketweb') return <TicketWebListings />;
  if (tab === 'activity') return <Activity />;
  if (tab === 'user-access') return <UserAccess />;
  if (tab === 'recently-deleted') return <RecentlyDeleted />;
  return <TabPlaceholder />;
}

function LandingRedirect() {
  const { user, isAuthenticated, logout } = useAuth();
  const [tenants, setTenants] = useState(null);
  const [tenantsError, setTenantsError] = useState(null);

  useEffect(() => {
    apiFetch('/api/me/tenants')
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setTenants(d); })
      .catch((e) => setTenantsError(e.message));
  }, [user]);

  // Signed out → client-side SSO login.
  if (!isAuthenticated && !tenants?.anonymous) {
    return <Navigate to="/login" replace />;
  }

  // Tenant list still loading
  if (!tenants && !tenantsError) {
    return <div className="min-h-screen flex items-center justify-center"><LoadingState label="Loading tenants…" /></div>;
  }

  // Authenticated but zero tenants → "No access" page
  if (isAuthenticated && !tenants?.anonymous && tenants?.tenants?.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-xl mb-2">No access</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Your account ({user?.email || user?.sub}) isn't a member of any tenant yet.
            Ask a Teliance admin to grant you access.
          </p>
          <button onClick={logout} className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-accent">Sign out</button>
        </div>
      </div>
    );
  }

  // Has tenants — auto-redirect to first tenant's calendar
  const first = tenants?.tenants?.[0];
  if (first) {
    return <Navigate to={`/${first.slug}/calendar`} replace />;
  }

  // Anonymous (dev) mode fallback — list visible tenants
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-xl mb-4">Teliance Events</h1>
        <p className="text-sm text-muted-foreground mb-4">Pick a tenant:</p>
        <div className="space-x-2">
          {tenants?.tenants?.map((t) => (
            <Link key={t.slug} to={`/${t.slug}/calendar`} className="underline">{t.slug}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<LandingRedirect />} />
      <Route path="/:tenantSlug" element={<Layout><TenantHome /></Layout>} />
      {/* Standalone full-screen app, NO admin Layout chrome. Launched from the
          menu in its own tab; PWA-installable. Static segment outranks :tab. */}
      <Route path="/:tenantSlug/event-dashboard" element={<EventDashboard />} />
      <Route path="/:tenantSlug/:tab" element={<Layout><TabRouter /></Layout>} />
      <Route path="/:tenantSlug/event/:eventId" element={<Layout><EventDetail /></Layout>} />
      <Route path="/:tenantSlug/event/:eventId/universe" element={<Layout><UniverseOverrides /></Layout>} />
      <Route path="/:tenantSlug/event/:eventId/urvenue" element={<Layout><UrVenueOverrides /></Layout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
